import { Share2, Check } from "lucide-react";
import { proxyImg } from "@/utils/imageProxy";

const BRAND_LOGO = proxyImg("https://customer-assets.emergentagent.com/job_cake-design-studio-5/artifacts/5xphm2ah_Sugar%20Bouquet%20Logo.png", 64, 80);

const SIZE_PRICING = {
  "8 Servings": { original: 1850, discounted: 1758 },
  "12 Servings": { original: 2100, discounted: 1995 },
  "16 Servings": { original: 2650, discounted: 2518 },
  "24 Servings": { original: 3400, discounted: 3230 },
};

const NO_DISCOUNT_TYPES = new Set(["Combo", "Ring Shape", "12 Pcs", "2 Tier (min 3kg)"]);

function DesignPrice({ design }) {
  const sizes = design.available_sizes;
  const isSpecialType = sizes?.length === 1 && NO_DISCOUNT_TYPES.has(sizes[0]);

  if (design.price && isSpecialType) {
    return <span className="text-[10px] font-semibold text-gray-700">&#8377;{design.price.toLocaleString("en-IN")}</span>;
  }

  if (design.price) {
    return (
      <>
        <span className="text-[10px] text-gray-400 line-through">&#8377;{design.price.toLocaleString("en-IN")}</span>
        <span className="text-[10px] font-semibold text-red-600">&#8377;{Math.round(design.price * 0.95).toLocaleString("en-IN")}</span>
        <span className="text-[9px] bg-red-500 text-white px-1 py-0.5 rounded font-bold">5% OFF</span>
      </>
    );
  }

  if (sizes?.length === 1) {
    const pricing = SIZE_PRICING[sizes[0]];
    if (pricing) {
      return (
        <>
          <span className="text-[10px] text-gray-400 line-through">&#8377;{pricing.original.toLocaleString("en-IN")}</span>
          <span className="text-[10px] font-semibold text-red-600">&#8377;{pricing.discounted.toLocaleString("en-IN")}</span>
          <span className="text-[9px] bg-red-500 text-white px-1 py-0.5 rounded font-bold">5% OFF</span>
        </>
      );
    }
  }

  return (
    <>
      <span className="text-[10px] text-gray-400 line-through">From &#8377;1,850</span>
      <span className="text-[10px] font-semibold text-red-600">&#8377;1,758</span>
      <span className="text-[9px] bg-red-500 text-white px-1 py-0.5 rounded font-bold">5% OFF</span>
    </>
  );
}

function handleWhatsAppShareDesign(e, design) {
  e.stopPropagation();
  const text = `Hey! Look at this beautiful "${design.name}" from Sugar Bouquet India! ${design.description} 100% eggless designer buttercream flower cakes. Order now!`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
}

function DesignCard({ design, isSelected, onSelect }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-xl border-2 bg-white transition-all duration-300 cursor-pointer ${
        isSelected
          ? "border-black shadow-lg ring-2 ring-black/10"
          : "border-gray-100 hover:border-gray-300 hover:shadow-lg"
      }`}
      onClick={() => onSelect(design)}
      data-testid={`design-card-${design.id}`}
    >
      <div className="aspect-square w-full overflow-hidden bg-gray-50 relative">
        <img
          src={proxyImg(design.image)}
          alt={design.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
          width={400}
          height={400}
        />
        {isSelected && (
          <div className="absolute top-2 left-2 h-6 w-6 rounded-full bg-black text-white flex items-center justify-center" data-testid={`selected-badge-${design.id}`}>
            <Check className="w-3.5 h-3.5" />
          </div>
        )}
        <button
          onClick={(e) => handleWhatsAppShareDesign(e, design)}
          className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 backdrop-blur-sm text-gray-600 hover:bg-green-500 hover:text-white flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 shadow-sm"
          data-testid={`whatsapp-share-design-${design.id}`}
          title="Share on WhatsApp"
        >
          <Share2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2.5">
        <h4 className={`font-semibold text-xs line-clamp-1 mb-1 ${isSelected ? "text-black underline" : "text-gray-900 group-hover:underline"} decoration-1 underline-offset-2`}>
          {design.name}
        </h4>
        <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed">{design.description}</p>
        <div className="mt-1.5 flex items-center gap-1.5">
          <DesignPrice design={design} />
        </div>
      </div>
    </div>
  );
}

export default function DesignGallery({ collectionName, designs, onDesignSelect, selectedDesignId }) {
  if (!designs || designs.length === 0) return null;

  return (
    <div className="mt-6 animate-fade-in-up" data-testid="design-gallery">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-8 w-8 rounded-full overflow-hidden bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
          <img src={BRAND_LOGO} alt="Sugar Bouquet" className="w-full h-full object-contain p-0.5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{collectionName} Collection</h3>
          <p className="text-xs text-gray-500">Tap a design to select it</p>
        </div>
      </div>
      <div className="ml-11 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {designs.map((design) => (
          <DesignCard
            key={design.id}
            design={design}
            isSelected={selectedDesignId === design.id}
            onSelect={onDesignSelect}
          />
        ))}
      </div>
    </div>
  );
}
